import { fetchJSON as fetchAPI } from './client';

const WORKFLOW_BASE = import.meta.env.VITE_WORKFLOW_API ?? 'http://localhost:8106';

export type SagaInstance = {
  id: string;
  process_id: string;
  tenant_id: string;
  organization_id: string;
  correlation_id: string;
  trace_id?: string;
  status: string;
  current_step: number;
  checkpoint_step: number;
  steps: Array<{
    id: string;
    name: string;
    module_id: string;
    status: string;
    attempts: number;
    error?: string;
  }>;
  error?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
};

export type SagaHistoryEntry = {
  id: string;
  saga_id: string;
  action: string;
  from_state?: string;
  to_state?: string;
  details?: string;
  created_at: string;
};

export type SagaDashboard = {
  metrics: {
    total_instances: number;
    running: number;
    completed: number;
    failed: number;
    compensating: number;
    recovery_pending: number;
    by_process: Record<string, number>;
  };
  recovery_pending: Array<{ id: string; saga_id: string; reason: string; status: string }>;
  processes: number;
  generated_at: string;
};

function fetchSaga<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(WORKFLOW_BASE, path, init);
}

export const sagaApi = {
  list: (params?: { status?: string; process_id?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.process_id) q.set('process_id', params.process_id);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return fetchSaga<{ sagas: SagaInstance[]; definitions: unknown[]; count: number }>(
      `/api/v1/sagas${qs ? `?${qs}` : ''}`,
    );
  },

  get: (id: string) =>
    fetchSaga<{ saga: SagaInstance; events: unknown[]; checkpoint: unknown }>(`/api/v1/sagas/${id}`),

  dashboard: () => fetchSaga<SagaDashboard>('/api/v1/sagas/dashboard'),

  history: (limit = 100) =>
    fetchSaga<{ history: SagaHistoryEntry[]; count: number }>(`/api/v1/sagas/history?limit=${limit}`),

  resume: (id: string) =>
    fetchSaga<{ data: SagaInstance }>(`/api/v1/sagas/${id}/resume`, { method: 'POST' }),

  cancel: (id: string) =>
    fetchSaga<SagaInstance>(`/api/v1/sagas/${id}/cancel`, { method: 'POST' }),

  retry: (id: string) =>
    fetchSaga<{ data: SagaInstance }>(`/api/v1/sagas/${id}/retry`, { method: 'POST' }),
};
